import { Either, Option, Schema } from "effect";
import type { LoopDocument, OutcomeHead } from "./types.ts";

// ---------------------------------------------------------------------------
// The structured-outcomes seam. A loop (and a terminal) may declare what its
// outcome IS: `outcome: { head, schema }`. `head` is the projected DB-side
// record; `schema` is a JSON Schema — restricted to the subset below — for the
// body, or a `$ref` to a schema declared in a document's `schemas:` map.
//
// Everything here is pure: no Effect requirement, no interpreter, no IO. The
// interpreter's stepping is untouched by this module; it exists so the loader
// (`validate.ts`), the host materialiser and the port admission checks all
// agree on one definition of "does this body match its declared type".
// ---------------------------------------------------------------------------

/**
 * The declared outcome of a document or a node: the head fields projected into
 * the DB, and the JSON Schema (subset) of the body.
 */
export const OutcomeSpec = Schema.Struct({
  head: Schema.optional(Schema.Array(Schema.String)),
  /** A JSON Schema (subset) or `{ $ref: "<loop>@<version>#<name>" | "#/schemas/<name>" }`. */
  schema: Schema.optional(Schema.Unknown),
});

/** The decoded {@link OutcomeSpec}. */
export type OutcomeSpec = typeof OutcomeSpec.Type;

/** The verdict a loop routes when its body does not match its declared schema. */
export const OUTCOME_INVALID = "outcome_invalid" as const;

/** One structural violation: a JSON pointer, the keyword (or `subset` / `ref`), and detail. */
export interface Violation {
  readonly path: string;
  readonly rule: string;
  readonly message: string;
}

/** Resolves a `$ref` that is not local to the schema being checked. */
export type SchemaResolver = (ref: string) => Option.Option<unknown>;

/** One `$ref` found inside a schema, with the JSON pointer it sits at. */
export interface SchemaRef {
  readonly path: string;
  readonly ref: string;
}

const violation = (path: string, rule: string, message: string): Violation => ({ path, rule, message });

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isStringArray = (value: unknown): value is ReadonlyArray<string> =>
  Array.isArray(value) && value.every((entry) => typeof entry === "string");

/** The seven type names the subset admits. `integer` is a number with no fraction. */
const TYPE_NAMES = new Set(["object", "array", "string", "number", "integer", "boolean", "null"]);

/** The keywords the subset admits — anything else is a `subset` violation. */
const SUBSET_KEYWORDS = new Set([
  "type",
  "properties",
  "required",
  "additionalProperties",
  "items",
  "additionalItems",
  "enum",
  "const",
  "minLength",
  "maxLength",
  "minimum",
  "maximum",
  "exclusiveMinimum",
  "exclusiveMaximum",
  "minItems",
  "maxItems",
  "pattern",
  "anyOf",
  "$defs",
  "$ref",
]);

/** Annotation keywords a schema may carry; they constrain nothing and are ignored. */
const IGNORED_KEYWORDS = new Set(["title", "description", "$schema", "$id"]);

// --- the subset check ------------------------------------------------------

const compiles = (pattern: string): boolean => Either.isRight(Either.try(() => new RegExp(pattern)));

type KeywordCheck = (value: unknown, path: string, out: Array<Violation>) => void;

const subsetMap: KeywordCheck = (value, path, out) => {
  if (!isRecord(value)) {
    out.push(violation(path, "subset", "expected an object of subschemas"));
    return;
  }
  for (const [name, sub] of Object.entries(value)) {
    subsetAt(sub, `${path}/${name}`, out);
  }
};

const subsetList: KeywordCheck = (value, path, out) => {
  if (!Array.isArray(value) || value.length === 0) {
    out.push(violation(path, "subset", "expected a non-empty array of subschemas"));
    return;
  }
  value.forEach((sub, index) => subsetAt(sub, `${path}/${index}`, out));
};

const KEYWORD_CHECKS: Readonly<Record<string, KeywordCheck>> = {
  type: (value, path, out) => {
    const names = isStringArray(value) ? value : typeof value === "string" ? [value] : undefined;
    if (names === undefined || names.length === 0 || names.some((name) => !TYPE_NAMES.has(name))) {
      out.push(violation(path, "subset", "`type` must be a subset type name or a list of them"));
    }
  },
  properties: subsetMap,
  $defs: subsetMap,
  anyOf: subsetList,
  items: (value, path, out) => {
    if (Array.isArray(value)) {
      value.forEach((sub, index) => subsetAt(sub, `${path}/${index}`, out));
      return;
    }
    subsetAt(value, path, out);
  },
  required: (value, path, out) => {
    if (!isStringArray(value)) {
      out.push(violation(path, "subset", "`required` must be an array of property names"));
    }
  },
  additionalProperties: (value, path, out) => {
    if (typeof value !== "boolean") {
      out.push(violation(path, "subset", "`additionalProperties` must be a boolean"));
    }
  },
  // A closed tuple writes `additionalItems: false` (what `Schema.Tuple` emits);
  // an open one gives the rest elements a schema.
  additionalItems: (value, path, out) => {
    if (typeof value !== "boolean") {
      subsetAt(value, path, out);
    }
  },
  enum: (value, path, out) => {
    if (!Array.isArray(value) || value.length === 0) {
      out.push(violation(path, "subset", "`enum` must be a non-empty array"));
    }
  },
  pattern: (value, path, out) => {
    if (typeof value !== "string" || !compiles(value)) {
      out.push(violation(path, "subset", "`pattern` must be a valid regular expression"));
    }
  },
  $ref: (value, path, out) => {
    if (typeof value !== "string") {
      out.push(violation(path, "subset", "`$ref` must be a string"));
      return;
    }
    if (value.includes("://")) {
      out.push(violation(path, "ref", "a `$ref` may not name a URL"));
    }
  },
};

const NUMERIC_KEYWORDS = new Set([
  "minLength",
  "maxLength",
  "minimum",
  "maximum",
  "exclusiveMinimum",
  "exclusiveMaximum",
  "minItems",
  "maxItems",
]);

const subsetAt = (schema: unknown, path: string, out: Array<Violation>): void => {
  if (!isRecord(schema)) {
    out.push(violation(path, "subset", "a schema must be an object"));
    return;
  }
  for (const [key, value] of Object.entries(schema)) {
    if (IGNORED_KEYWORDS.has(key)) {
      continue;
    }
    if (!SUBSET_KEYWORDS.has(key)) {
      out.push(violation(`${path}/${key}`, "subset", `keyword \`${key}\` is outside the JSON Schema subset`));
      continue;
    }
    const keywordPath = `${path}/${key}`;
    if (NUMERIC_KEYWORDS.has(key)) {
      if (typeof value !== "number") {
        out.push(violation(keywordPath, "subset", `\`${key}\` must be a number`));
      }
      continue;
    }
    KEYWORD_CHECKS[key]?.(value, keywordPath, out);
  }
};

/** Every keyword outside the subset, reported at the JSON pointer it sits at. */
export const checkSubset = (schema: unknown): ReadonlyArray<Violation> => {
  const out: Array<Violation> = [];
  subsetAt(schema, "", out);
  return out;
};

// --- `$ref` walking and resolution -----------------------------------------

const walkRefs = (schema: unknown, path: string, out: Array<SchemaRef>): void => {
  if (Array.isArray(schema)) {
    schema.forEach((entry, index) => walkRefs(entry, `${path}/${index}`, out));
    return;
  }
  if (!isRecord(schema)) {
    return;
  }
  for (const [key, value] of Object.entries(schema)) {
    if (key === "$ref" && typeof value === "string") {
      out.push({ path: `${path}/$ref`, ref: value });
      continue;
    }
    walkRefs(value, `${path}/${key}`, out);
  }
};

/** Every `$ref` string inside a schema, paired with its JSON pointer. */
export const collectRefs = (schema: unknown): ReadonlyArray<SchemaRef> => {
  const out: Array<SchemaRef> = [];
  walkRefs(schema, "", out);
  return out;
};

const unescapePointer = (segment: string): string => segment.replaceAll("~1", "/").replaceAll("~0", "~");

const pointer = (root: unknown, ref: string): Option.Option<unknown> => {
  const segments = ref.slice(1).split("/").filter((segment) => segment.length > 0).map(unescapePointer);
  let current: unknown = root;
  for (const segment of segments) {
    if (!isRecord(current) || !(segment in current)) {
      return Option.none();
    }
    current = current[segment];
  }
  return Option.some(current);
};

/** `<loop>[@<version>|@latest]#<name>` — a schema declared by another document's `schemas:` map. */
const CROSS_REF = /^([^#@]+)(?:@(\d+|latest))?#(.+)$/;

const pickDocument = (
  resolvable: ReadonlyArray<LoopDocument>,
  name: string,
  spec: string | undefined,
): LoopDocument | undefined => {
  const group = resolvable.filter((candidate) => candidate.loop === name);
  if (spec === undefined || spec === "latest") {
    return group.reduce<LoopDocument | undefined>(
      (best, candidate) => best === undefined || (candidate.version ?? 0) > (best.version ?? 0) ? candidate : best,
      undefined,
    );
  }
  return group.find((candidate) => candidate.version === Number(spec));
};

const LOCAL_SCHEMAS = "#/schemas/";

/**
 * Resolve one `$ref`: a `#/schemas/<name>` entry of `document`, any other local
 * JSON pointer against `root` (that is how `#/$defs/<name>` resolves), or a
 * cross-document `<loop>@<version>#<name>`.
 */
export const resolveRefIn = (
  root: unknown,
  document: LoopDocument,
  ref: string,
  resolvable: ReadonlyArray<LoopDocument>,
): Either.Either<unknown, Violation> => {
  if (ref.startsWith(LOCAL_SCHEMAS)) {
    const name = ref.slice(LOCAL_SCHEMAS.length);
    const local = (document.schemas ?? {})[name];
    return local === undefined
      ? Either.left(violation("", "ref", `\`${ref}\` names no entry in this document's \`schemas:\``))
      : Either.right(local);
  }
  if (ref.startsWith("#")) {
    return Option.match(pointer(root, ref), {
      onNone: (): Either.Either<unknown, Violation> =>
        Either.left(violation("", "ref", `\`${ref}\` resolves to nothing`)),
      onSome: (value): Either.Either<unknown, Violation> => Either.right(value),
    });
  }
  const parsed = CROSS_REF.exec(ref);
  if (parsed === null) {
    return Either.left(violation("", "ref", `\`${ref}\` is not a \`<loop>@<version>#<name>\` reference`));
  }
  const target = pickDocument(resolvable, parsed[1], parsed[2]);
  if (target === undefined) {
    return Either.left(violation("", "ref", `\`${ref}\` names no resolvable loop document`));
  }
  const schema = (target.schemas ?? {})[parsed[3]];
  return schema === undefined
    ? Either.left(violation("", "ref", `\`${ref}\` names no entry in the \`schemas:\` of \`${parsed[1]}\``))
    : Either.right(schema);
};

/**
 * Resolve a declared `outcome.schema`: a bare schema comes back as itself, a
 * `{ $ref }` is resolved through `document` and `resolvable`.
 */
export const resolveOutcomeSchema = (
  document: LoopDocument,
  ref: unknown,
  resolvable: ReadonlyArray<LoopDocument>,
): Either.Either<unknown, Violation> => {
  if (!isRecord(ref)) {
    return Either.right(ref);
  }
  const target = ref["$ref"];
  if (target === undefined) {
    return Either.right(ref);
  }
  if (typeof target !== "string") {
    return Either.left(violation("/$ref", "ref", "`$ref` must be a string"));
  }
  return resolveRefIn(undefined, document, target, resolvable);
};

/**
 * One step of {@link inlineLocalSchemaRefs}; `seen` is the chain of
 * `#/schemas/<name>` entries already entered on this path, so a cycle is
 * reported instead of recurred into.
 */
const inlineAt = (
  document: LoopDocument,
  value: unknown,
  seen: ReadonlyArray<string>,
): Either.Either<unknown, Violation> => {
  if (Array.isArray(value)) {
    const out: Array<unknown> = [];
    for (const entry of value) {
      const inlined = inlineAt(document, entry, seen);
      if (Either.isLeft(inlined)) {
        return inlined;
      }
      out.push(inlined.right);
    }
    return Either.right(out);
  }
  if (!isRecord(value)) {
    return Either.right(value);
  }
  const ref = value["$ref"];
  if (typeof ref === "string" && ref.startsWith(LOCAL_SCHEMAS)) {
    const name = ref.slice(LOCAL_SCHEMAS.length);
    if (seen.includes(name)) {
      return Either.left(
        violation(
          "",
          "ref",
          `\`${ref}\` is a cycle through this document's \`schemas:\` (${
            [...seen, name].join(
              " → ",
            )
          })`,
        ),
      );
    }
    return resolveRefIn(undefined, document, ref, []).pipe(
      Either.flatMap((entry) => inlineAt(document, entry, [...seen, name])),
    );
  }
  const out: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value)) {
    const inlined = inlineAt(document, entry, seen);
    if (Either.isLeft(inlined)) {
      return inlined;
    }
    out[key] = inlined.right;
  }
  return Either.right(out);
};

/**
 * Replace every `{ "$ref": "#/schemas/<name>" }` anywhere inside `value` by the
 * entry `document` declares under that name, recursively (a cycle is a
 * violation, not a hang).
 *
 * `#/schemas/<name>` is relative to the LOOP DOCUMENT, and the document is held
 * only by the dispatching harness: a consumer handed the bare reference — the
 * definer reading its `schema` input, the callback admission validating against
 * the recorded declaration — can resolve nothing. So the harness inlines before
 * it dispatches. Every OTHER `$ref` is left exactly as it is: an entry's own
 * `#/$defs/…` pointers stay valid because the entry becomes the root they
 * resolve against.
 *
 * @since 1.0.0
 */
export const inlineLocalSchemaRefs = (
  document: LoopDocument,
  value: unknown,
): Either.Either<unknown, Violation> => inlineAt(document, value, []);

/** A resolver over a fixed document set — the `refs` argument the checks below take. */
export const schemaResolver = (documents: ReadonlyArray<LoopDocument>): SchemaResolver => (ref) => {
  for (const document of documents) {
    const resolved = resolveRefIn(undefined, document, ref, documents);
    if (Either.isRight(resolved)) {
      return Option.some(resolved.right);
    }
  }
  return Option.none();
};

// --- body validation -------------------------------------------------------

interface Resolved {
  readonly schema: Record<string, unknown> | undefined;
  readonly violation: Violation | undefined;
}

const UNCONSTRAINED: Resolved = { schema: undefined, violation: undefined };

/** Follow `$ref` hops until a plain schema is reached; a cycle stops constraining. */
const resolveSchema = (schema: unknown, path: string, root: unknown, refs: SchemaResolver): Resolved => {
  const seen = new Set<string>();
  let current = schema;
  while (isRecord(current)) {
    const ref = current["$ref"];
    if (typeof ref !== "string") {
      return { schema: current, violation: undefined };
    }
    if (seen.has(ref)) {
      return UNCONSTRAINED;
    }
    seen.add(ref);
    const local = ref.startsWith("#") ? pointer(root, ref) : Option.none();
    const target = Option.isSome(local) ? local : refs(ref);
    if (Option.isNone(target)) {
      return { schema: undefined, violation: violation(path, "ref", `\`${ref}\` resolves to nothing`) };
    }
    current = target.value;
  }
  return UNCONSTRAINED;
};

const typeList = (value: unknown): ReadonlyArray<string> =>
  isStringArray(value) ? value : typeof value === "string" ? [value] : [];

const matchesType = (name: string, value: unknown): boolean =>
  name === "object"
    ? isRecord(value)
    : name === "array"
    ? Array.isArray(value)
    : name === "integer"
    ? typeof value === "number" && Number.isInteger(value)
    : name === "number"
    ? typeof value === "number" && Number.isFinite(value)
    : name === "null"
    ? value === null
    : typeof value === name;

const sameJson = (left: unknown, right: unknown): boolean => {
  if (left === right) {
    return true;
  }
  if (Array.isArray(left) && Array.isArray(right)) {
    return left.length === right.length && left.every((entry, index) => sameJson(entry, right[index]));
  }
  if (isRecord(left) && isRecord(right)) {
    const keys = Object.keys(left);
    return keys.length === Object.keys(right).length &&
      keys.every((key) => key in right && sameJson(left[key], right[key]));
  }
  return false;
};

const numberOf = (value: unknown): number | undefined => typeof value === "number" ? value : undefined;

interface BodyContext {
  readonly root: unknown;
  readonly refs: SchemaResolver;
  readonly out: Array<Violation>;
}

const bodyObject = (
  schema: Record<string, unknown>,
  value: Record<string, unknown>,
  path: string,
  context: BodyContext,
): void => {
  const properties = isRecord(schema["properties"]) ? schema["properties"] : {};
  for (const field of typeList(schema["required"])) {
    if (!(field in value)) {
      context.out.push(violation(`${path}/${field}`, "required", `\`${field}\` is required`));
    }
  }
  for (const [name, sub] of Object.entries(properties)) {
    if (name in value) {
      bodyAt(sub, value[name], `${path}/${name}`, context);
    }
  }
  if (schema["additionalProperties"] === false) {
    for (const key of Object.keys(value)) {
      if (!(key in properties)) {
        context.out.push(violation(`${path}/${key}`, "additionalProperties", `\`${key}\` is not a declared property`));
      }
    }
  }
};

const bodyArray = (
  schema: Record<string, unknown>,
  value: ReadonlyArray<unknown>,
  path: string,
  context: BodyContext,
): void => {
  const items = schema["items"];
  if (Array.isArray(items)) {
    items.forEach((sub, index) => {
      if (index < value.length) {
        bodyAt(sub, value[index], `${path}/${index}`, context);
      }
    });
  } else if (items !== undefined) {
    value.forEach((entry, index) => bodyAt(items, entry, `${path}/${index}`, context));
  }
  const min = numberOf(schema["minItems"]);
  if (min !== undefined && value.length < min) {
    context.out.push(violation(path, "minItems", `expected at least ${min} item(s)`));
  }
  const max = numberOf(schema["maxItems"]);
  if (max !== undefined && value.length > max) {
    context.out.push(violation(path, "maxItems", `expected at most ${max} item(s)`));
  }
  if (Array.isArray(items) && schema["additionalItems"] === false && value.length > items.length) {
    context.out.push(
      violation(path, "additionalItems", `expected at most ${items.length} item(s): the tuple is closed`),
    );
  }
};

const bodyString = (schema: Record<string, unknown>, value: string, path: string, context: BodyContext): void => {
  const min = numberOf(schema["minLength"]);
  if (min !== undefined && value.length < min) {
    context.out.push(violation(path, "minLength", `expected at least ${min} character(s)`));
  }
  const max = numberOf(schema["maxLength"]);
  if (max !== undefined && value.length > max) {
    context.out.push(violation(path, "maxLength", `expected at most ${max} character(s)`));
  }
  const pattern = schema["pattern"];
  if (typeof pattern === "string" && !new RegExp(pattern).test(value)) {
    context.out.push(violation(path, "pattern", `expected a string matching ${pattern}`));
  }
};

const bodyNumber = (schema: Record<string, unknown>, value: number, path: string, context: BodyContext): void => {
  const min = numberOf(schema["minimum"]);
  if (min !== undefined && value < min) {
    context.out.push(violation(path, "minimum", `expected at least ${min}`));
  }
  const max = numberOf(schema["maximum"]);
  if (max !== undefined && value > max) {
    context.out.push(violation(path, "maximum", `expected at most ${max}`));
  }
  const exclusiveMin = numberOf(schema["exclusiveMinimum"]);
  if (exclusiveMin !== undefined && value <= exclusiveMin) {
    context.out.push(violation(path, "exclusiveMinimum", `expected greater than ${exclusiveMin}`));
  }
  const exclusiveMax = numberOf(schema["exclusiveMaximum"]);
  if (exclusiveMax !== undefined && value >= exclusiveMax) {
    context.out.push(violation(path, "exclusiveMaximum", `expected less than ${exclusiveMax}`));
  }
};

const bodyAt = (schema: unknown, value: unknown, path: string, context: BodyContext): void => {
  const resolved = resolveSchema(schema, path, context.root, context.refs);
  if (resolved.violation !== undefined) {
    context.out.push(resolved.violation);
    return;
  }
  if (resolved.schema === undefined) {
    return;
  }
  const current = resolved.schema;
  const types = typeList(current["type"]);
  if (types.length > 0 && !types.some((name) => matchesType(name, value))) {
    context.out.push(violation(path, "type", `expected ${types.join(" | ")}`));
    return;
  }
  const members = current["enum"];
  if (Array.isArray(members) && !members.some((member) => sameJson(member, value))) {
    context.out.push(violation(path, "enum", `\`${JSON.stringify(value)}\` is not one of the declared values`));
  }
  if ("const" in current && !sameJson(current["const"], value)) {
    context.out.push(violation(path, "const", `expected ${JSON.stringify(current["const"])}`));
  }
  const branches = current["anyOf"];
  if (Array.isArray(branches)) {
    const matched = branches.some((branch) => {
      const probe: Array<Violation> = [];
      bodyAt(branch, value, path, { ...context, out: probe });
      return probe.length === 0;
    });
    if (!matched) {
      context.out.push(violation(path, "anyOf", "matches no branch of `anyOf`"));
    }
  }
  if (isRecord(value)) {
    bodyObject(current, value, path, context);
  }
  if (Array.isArray(value)) {
    bodyArray(current, value, path, context);
  }
  if (typeof value === "string") {
    bodyString(current, value, path, context);
  }
  if (typeof value === "number") {
    bodyNumber(current, value, path, context);
  }
};

/**
 * Walk a producer schema down an output sub-path (`${nodes.p.output.tasks}`)
 * through its `properties`, resolving a `$ref` at each hop.
 */
export const propertyAt = (
  schema: unknown,
  segments: ReadonlyArray<string>,
  refs: SchemaResolver,
): Either.Either<unknown, Violation> => {
  let current: unknown = schema;
  let path = "";
  for (const segment of segments) {
    const resolved = resolveSchema(current, path, schema, refs);
    if (resolved.violation !== undefined) {
      return Either.left(resolved.violation);
    }
    const properties = resolved.schema === undefined ? undefined : resolved.schema["properties"];
    const next = isRecord(properties) ? properties[segment] : undefined;
    path = `${path}/${segment}`;
    if (next === undefined) {
      return Either.left(violation(path, "properties", `declares no \`${segment}\``));
    }
    current = next;
  }
  return Either.right(current);
};

/** The `items` schema of an array producer — what a `for_each`'s `${item}` is bound to. */
export const itemsOf = (schema: unknown, refs: SchemaResolver): Option.Option<unknown> => {
  const resolved = resolveSchema(schema, "", schema, refs);
  const items = resolved.schema === undefined ? undefined : resolved.schema["items"];
  return items === undefined ? Option.none() : Option.some(items);
};

/** Validate a body against a subset schema; `refs` resolves non-local `$ref`s. */
export const validateBody = (
  schema: unknown,
  value: unknown,
  refs: SchemaResolver,
): ReadonlyArray<Violation> => {
  const out: Array<Violation> = [];
  bodyAt(schema, value, "", { root: schema, refs, out });
  return out;
};

/**
 * Every field a spec declares in its `head:` must be present in the projected head.
 *
 * `head` is spelled `?: ... | undefined` so the parameter stays assignable from a
 * decoded {@link OutcomeSpec} — `Schema.optional` widens to an explicit
 * `| undefined`, which `exactOptionalPropertyTypes` (on in `@fm/web`, which
 * compiles these sources through its own project) otherwise rejects.
 */
export const validateHead = (
  spec: { readonly head?: ReadonlyArray<string> | undefined },
  head: OutcomeHead,
): ReadonlyArray<Violation> =>
  (spec.head ?? []).filter((field) => !(field in head)).map((field) =>
    violation(`/${field}`, "head", `the head field \`${field}\` is missing`)
  );

// --- assignability ---------------------------------------------------------

const typeAccepts = (consumer: string, producer: string): boolean =>
  consumer === producer || (consumer === "number" && producer === "integer");

const assignableAt = (
  producer: unknown,
  consumer: unknown,
  path: string,
  roots: { readonly producer: unknown; readonly consumer: unknown },
  refs: SchemaResolver,
  out: Array<Violation>,
): void => {
  const wanted = resolveSchema(consumer, path, roots.consumer, refs);
  if (wanted.violation !== undefined) {
    out.push(wanted.violation);
    return;
  }
  if (wanted.schema === undefined) {
    return;
  }
  const given = resolveSchema(producer, path, roots.producer, refs);
  if (given.violation !== undefined) {
    out.push(given.violation);
    return;
  }
  if (given.schema === undefined) {
    return;
  }
  const wantTypes = typeList(wanted.schema["type"]);
  const gaveTypes = typeList(given.schema["type"]);
  if (
    wantTypes.length > 0 && gaveTypes.length > 0
    && !gaveTypes.every((name) => wantTypes.some((want) => typeAccepts(want, name)))
  ) {
    out.push(violation(path, "type", `the producer's ${gaveTypes.join(" | ")} is not a ${wantTypes.join(" | ")}`));
  }
  const guaranteed = new Set(typeList(given.schema["required"]));
  for (const field of typeList(wanted.schema["required"])) {
    if (!guaranteed.has(field)) {
      out.push(violation(`${path}/${field}`, "required", `the producer does not guarantee the required \`${field}\``));
    }
  }
  const wantProperties = isRecord(wanted.schema["properties"]) ? wanted.schema["properties"] : {};
  const gaveProperties = isRecord(given.schema["properties"]) ? given.schema["properties"] : {};
  for (const [name, sub] of Object.entries(wantProperties)) {
    if (name in gaveProperties) {
      assignableAt(gaveProperties[name], sub, `${path}/${name}`, roots, refs, out);
    }
  }
  if (isRecord(wanted.schema["items"]) && isRecord(given.schema["items"])) {
    assignableAt(given.schema["items"], wanted.schema["items"], `${path}/items`, roots, refs, out);
  }
  const wantMembers = wanted.schema["enum"];
  if (Array.isArray(wantMembers)) {
    const gaveMembers = given.schema["enum"];
    const produced = Array.isArray(gaveMembers)
      ? gaveMembers
      : "const" in given.schema
      ? [given.schema["const"]]
      : undefined;
    if (produced === undefined) {
      out.push(violation(path, "enum", "the producer does not restrict the value to the consumer's `enum`"));
    } else {
      for (const member of produced) {
        if (!wantMembers.some((want) => sameJson(want, member))) {
          out.push(violation(path, "enum", `the producer may emit ${JSON.stringify(member)}`));
        }
      }
    }
  }
  if (wanted.schema["additionalProperties"] === false && given.schema["additionalProperties"] !== false) {
    out.push(violation(path, "additionalProperties", "the producer is open where the consumer is closed"));
  }
};

/**
 * Is a producer's schema structurally assignable to a consumer's? An untyped
 * consumer accepts anything, and a producer with no schema is never a finding.
 */
export const assignable = (
  producer: unknown,
  consumer: unknown,
  refs: SchemaResolver,
): ReadonlyArray<Violation> => {
  const out: Array<Violation> = [];
  assignableAt(producer, consumer, "", { producer, consumer }, refs, out);
  return out;
};
